import {Inline, Stack, Text} from '@treatwell/ui';
import {useTranslation} from 'react-i18next';
import {AvailabilityApiResponseSku} from '../../../server/routes/availability/types';
import {EmployeeSelect} from '../EmployeeSelect';
import {RouteAvailability} from '../Router';
import styles from './OrderItem.module.css';
import clsx from 'clsx';
import {trackOpenEmployeeList} from '../../utils/gtmTracking';
import {selectSelectedMultiEmployee} from '../../store/slices/selectors';
import {useAppSelector} from '../../hooks/store';

export interface Item {
  id: string;
  name: string;
  skus: Pick<
    AvailabilityApiResponseSku,
    'id' | 'name' | 'durationMinutesFormatted' | 'variantName' | 'employees'
  >[];
}

interface Props extends Item {
  headingSize?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  onRemove: (skuId: string) => void;
  isMultiEmployeeSelectEnabled?: boolean;
  onEmployeeSelect: ({
    menuItemId,
    employeeId,
  }: {
    menuItemId: string;
    employeeId: string;
  }) => void;
}

export const OrderItem = (props: Props) => {
  const {t} = useTranslation();
  const anyEmployeeLabel = t('employeeSelect.label.anyEmployee');
  const selectedMultiEmployee = useAppSelector(selectSelectedMultiEmployee);
  const employeeId = selectedMultiEmployee[props.id];

  const handleDropdownToggle = (expanded: boolean) => {
    if (expanded) {
      trackOpenEmployeeList(props.id);
    }
  };

  return (
    <Stack
      as="section"
      data-cy="OrderItem"
      space={props.isMultiEmployeeSelectEnabled ? '2md' : undefined}
    >
      <Stack space={props.isMultiEmployeeSelectEnabled ? 'base' : undefined}>
        {props.skus.map((sku) => {
          let skuName = sku.name || props.name;
          if (sku.variantName && sku.variantName !== skuName) {
            skuName = `${skuName} - ${sku.variantName}`;
          }
          if (sku.name) {
            skuName = `${props.name} - ${skuName}`;
          }
          const ariaLabel = t('basket.removeItem', {
            serviceName: skuName,
          });
          return (
            <Inline
              key={sku.id}
              justify="between"
              align="center"
              className={clsx(styles.skuColor, {
                [styles.sku]: !props.isMultiEmployeeSelectEnabled,
              })}
            >
              <Stack as="dl" className={styles.skuDetails}>
                <Text as="dt" data-cy="SkuName" className={styles.skuName}>
                  {skuName}
                </Text>
                <Text as="dd" type="caption">
                  {sku.durationMinutesFormatted}
                </Text>
              </Stack>
              <RouteAvailability>
                <button
                  data-cy="RemoveSku"
                  className={styles.removeButton}
                  type="button"
                  aria-label={ariaLabel}
                  onClick={() => {
                    props.onRemove(sku.id);
                  }}
                >
                  <Text
                    type={
                      props.isMultiEmployeeSelectEnabled
                        ? 'xlHeader'
                        : 'lgHeader'
                    }
                  >
                    +
                  </Text>
                </button>
              </RouteAvailability>
            </Inline>
          );
        })}
        {props.isMultiEmployeeSelectEnabled && (
          <>
            <EmployeeSelect
              employeeItems={props.skus[0].employees}
              labelId="employee-select-label"
              onSelected={(value: string) => {
                props.onEmployeeSelect({
                  menuItemId: props.id,
                  employeeId: value,
                });
              }}
              anyEmployeeLabel={anyEmployeeLabel}
              initialSelectedEmployee={employeeId ? employeeId : undefined}
              onToggle={handleDropdownToggle}
            />
            <hr className={styles.divider} />
          </>
        )}
      </Stack>
    </Stack>
  );
};
